import * as React from 'react';
import { InlineDatePicker, MuiPickersUtilsProvider } from 'material-ui-pickers';
import { Duration, DateTime } from 'luxon';
import LuxonUtils from '@date-io/luxon';
import {
  Grid,
  Paper,
  Theme,
  Typography,
  createStyles,
  Button,
  Select,
  OutlinedInput,
  MenuItem,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import { Query } from 'react-apollo';
import { createStyled } from 'shared/hocs';
import {
  ORGANISATION_USAGE_QUERY,
  OrganisationUsageQueryResult,
} from 'organisations/queries';
import { useApolloClient } from '@apollo/react-hooks';
import gql from 'graphql-tag';

enum UsageType {
  Uploads = 'Upload',
  Realtime = 'Realtime',
  TrintLive = 'Trint Live',
}

interface Props {
  groupId: string;
}

interface State {
  start: DateTime;
  end: DateTime;
  usageType: UsageType;
}

interface OrganisationUsageQueryVars {
  organisationId: string;
  start: number;
  end: number;
  usageType: UsageType;
}

const GET_USAGE_CSV = gql`
  query UsageCsv($groupId: String!, $dateFrom: String!, $dateTo: String!) {
    usageCsv(groupId: $groupId, dateFrom: $dateFrom, dateTo: $dateTo)
  }
`;

const USAGE_TYPES = [
  { value: UsageType.Uploads, name: 'Upload' },
  { value: UsageType.Realtime, name: 'Realtime' },
  { value: UsageType.TrintLive, name: 'Trint Live' },
];

interface CsvDownloadProps {
  groupId: string;
  dateFrom: DateTime;
  dateTo: DateTime;
}

function CsvDownload({ groupId, dateFrom, dateTo }: CsvDownloadProps) {
  const client = useApolloClient();

  function fetchUsageCsv() {
    client
      .query<{ usageCsv: string }, CsvDownloadProps>({
        query: GET_USAGE_CSV,
        variables: { groupId, dateFrom, dateTo },
      })
      .then((result) => {
        window.location.assign(result.data.usageCsv);
      });
  }

  return (
    <>
      <Button
        type="button"
        variant="contained"
        color="primary"
        onClick={fetchUsageCsv}
      >
        Download CSV
      </Button>
      <Typography variant="caption" style={{ marginTop: '8px' }}>
        This will download a CSV file with the usage data for the selected
        organisation for the selected dates (all transcript types included)
      </Typography>
      <Typography variant="caption" style={{ marginTop: '8px' }}>
        Please note that the "workflow" column in the CSV will only populate for
        files created after February 5, 2025 onwards
      </Typography>
    </>
  );
}

function formatMinutes(usage: number) {
  const { seconds, minutes, hours } = Duration.fromObject({
    seconds: usage,
    minutes: 0,
    hours: 0,
  }).normalize();

  return `${hours} hours, ${minutes} minutes, ${seconds} seconds`;
}

function DisplayUsage({
  loading,
  error,
  data,
}: {
  loading?: boolean;
  error?: Error;
  data?: OrganisationUsageQueryResult;
}) {
  if (loading)
    return <CircularProgress style={{ marginTop: '16px' }} size="16px" />;
  if (error)
    return (
      <Typography variant="h5" style={{ marginTop: '16px' }}>
        Error: {error.message}
      </Typography>
    );
  if (data)
    return (
      <Typography variant="body1" style={{ marginTop: '16px' }}>
        {formatMinutes(data.getOrganisationUsageInSeconds)}
      </Typography>
    );
  return null;
}

class OrganisationUsage extends React.Component<Props, State> {
  state = {
    start: DateTime.fromJSDate(new Date()).startOf('month'),
    end: DateTime.fromJSDate(new Date()).endOf('month'),
    usageType: UsageType.Uploads,
  };

  formatMinutes(usage: number) {
    const { seconds, minutes, hours } = Duration.fromObject({
      seconds: usage,
      minutes: 0,
      hours: 0,
    }).normalize();

    return `${hours} hours, ${minutes} minutes, ${seconds} seconds`;
  }

  updateStartDate = (start: DateTime) => {
    this.setState({ start });
  };

  updateEndDate = (end: DateTime) => {
    this.setState({ end });
  };

  updateUsageType = (usageType: UsageType) => {
    this.setState({ usageType });
  };

  render() {
    const { groupId: organisationId } = this.props;
    const { start, end, usageType } = this.state;

    return (
      <Query<OrganisationUsageQueryResult, OrganisationUsageQueryVars>
        query={ORGANISATION_USAGE_QUERY}
        fetchPolicy="cache-and-network"
        variables={{
          organisationId,
          start: start.startOf('day').toMillis(),
          end: end.endOf('day').toMillis(),
          usageType,
        }}
      >
        {({ data, loading, error }) => {
          if (!data) return null;

          return (
            <Styled>
              {({ classes }) => (
                <main className={classes.layout}>
                  <Paper className={classes.paper}>
                    <Typography
                      className={classes.title}
                      component="h1"
                      variant="h5"
                    >
                      Organisation Usage for {data.group.metadata.name}
                    </Typography>
                    <Grid container spacing={24}>
                      <MuiPickersUtilsProvider utils={LuxonUtils}>
                        <Grid item xs={12} sm={6}>
                          <InlineDatePicker
                            keyboard
                            variant="outlined"
                            label="Start Date"
                            format="DDD"
                            value={start}
                            onChange={this.updateStartDate}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <InlineDatePicker
                            keyboard
                            variant="outlined"
                            label="End Date"
                            format="DDD"
                            value={end}
                            onChange={this.updateEndDate}
                          />
                        </Grid>
                      </MuiPickersUtilsProvider>
                      <Grid item xs={12} sm={12}>
                        {data && (
                          <CsvDownload
                            groupId={organisationId}
                            dateFrom={start}
                            dateTo={end}
                          />
                        )}
                      </Grid>
                    </Grid>

                    <Divider style={{ marginTop: 16, marginBottom: 16 }} />

                    <Grid item xs={12} sm={12}>
                      <Typography variant="h6">
                        Usage by transcript type
                      </Typography>
                      <Select
                        input={<OutlinedInput labelWidth={0} />}
                        value={usageType}
                        onChange={(e) =>
                          this.updateUsageType(e.target.value as UsageType)
                        }
                      >
                        {USAGE_TYPES.map((usageType) => {
                          return (
                            <MenuItem
                              key={usageType.value}
                              value={usageType.value}
                            >
                              {usageType.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {usageType === UsageType.TrintLive && (
                        <Typography
                          variant="caption"
                          style={{ marginTop: '4px' }}
                        >
                          Trint Live from Desktop minutes only available from
                          February 5, 2025 onwards
                        </Typography>
                      )}
                    </Grid>

                    <DisplayUsage loading={loading} error={error} data={data} />
                  </Paper>
                </main>
              )}
            </Styled>
          );
        }}
      </Query>
    );
  }
}

const Styled = createStyled((theme: Theme) => {
  return createStyles({
    layout: {
      width: 'auto',
      marginLeft: theme.spacing.unit * 2,
      marginRight: theme.spacing.unit * 2,
      [theme.breakpoints.up(600 + theme.spacing.unit * 2 * 2)]: {
        width: 600,
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    },
    paper: {
      marginTop: theme.spacing.unit * 3,
      marginBottom: theme.spacing.unit * 3,
      padding: theme.spacing.unit * 2,
      [theme.breakpoints.up(600 + theme.spacing.unit * 3 * 2)]: {
        marginTop: theme.spacing.unit * 6,
        marginBottom: theme.spacing.unit * 6,
        padding: theme.spacing.unit * 3,
      },
    },
    title: {
      marginBottom: theme.spacing.unit * 3,
    },
  });
});

export default OrganisationUsage;
